import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { catchError, Observable, throwError, mergeMap, take } from 'rxjs';
import { SessionService } from '@services/session.service';
import { combineLatest } from 'rxjs';
import { Router } from '@angular/router';
import { HttpContextToken } from '@angular/common/http';

interface AppHeaders {
  [name: string]: string | string[];
}

export const GAME_TYPE_OVERRIDE = new HttpContextToken<string | null>(
  () => null,
);

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  private session = inject(SessionService);
  private router = inject(Router);

  private isHandling403 = false;

  constructor() {}

  intercept(
    httpRequest: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    return combineLatest([
      this.session.client$.pipe(take(1)),
      this.session.gameType$.pipe(take(1)),
      this.session.authenticatedUser$.pipe(take(1)),
    ]).pipe(
      mergeMap(([client, gameType, authenticatedUser]) => {
        const headers: AppHeaders = {
          Authorization: 'Bearer ' + this.session.authToken,
        };

        const overriddenGameType = httpRequest.context.get(GAME_TYPE_OVERRIDE);
        const finalGameType = overriddenGameType ?? gameType;

        const pathMatch = /^https?:\/\/[^/]+(\/[^?#]*)/i.exec(httpRequest.url);
        const pathname = pathMatch ? pathMatch[1] : '';
        if (httpRequest.method === 'GET' && pathname === '/api/admin/client') {
          headers['x-client-uuid'] = authenticatedUser?.client?.uuid as string;
        } else if (client?.uuid) {
          headers['x-client-uuid'] = client.uuid;
        }
        if (finalGameType) {
          headers['x-game-type'] = finalGameType;
        }

        if (this.session.authToken) {
          httpRequest = httpRequest.clone({
            setHeaders: headers,
          });
        }

        return next.handle(httpRequest).pipe(
          catchError((error) => {
            if (error.status === 403 && !this.isHandling403) {
              // to avoid multiple redirects
              this.isHandling403 = true;
              // Clear session data
              this.session.resetDataOnLogin();
              this.router.navigate(['/login']).then(() => {
                this.isHandling403 = false;
              });
              return throwError(() => error);
            }
            return throwError(() => error);
          }),
        );
      }),
    );
  }
}
